/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-crf-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';

import {
    ADD_CRF_INITIAL_ERRORS,
    ADD_CRF_INITIAL_STATE,
    getInitialRules,
} from '@components/admin/add-crf-dialog/add-crf-dialog.data';
import {
    AddCrfDialogProps,
    AddCrfErrorState,
    AddCrfFormState,
} from '@components/admin/add-crf-dialog/add-crf-dialog.interface';
import AddRules from '@components/admin/add-rules';
import { ADD_RULES_FIELD_TYPES } from '@components/admin/add-rules/add-rules.data';
import { SVTRule } from '@components/admin/add-rules/rules-row/rules-row.interface';
import CopyButton from '@components/core/copy-button';
import StepsBox from '@components/core/steps-box';
import { useBoolean, useTranslation } from 'hooks';
import { GET_STUDY_CRFS, SAVE_STUDY_CRF } from 'queries/study-crfs';
import { checkConfigIdErrors, checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

import './styles.scss';

const pageTitles = (t: TFunction) => [`1. ${t('admin:crfTitle')}`, `2. ${t('admin:rulesTitle')}`];

const AddCrfDialog = ({ close, studyId, currentCrf = null, crfs, isSysAdmin }: AddCrfDialogProps) => {
    const { t } = useTranslation();
    const [state, setState] = useState<AddCrfFormState>({ ...ADD_CRF_INITIAL_STATE });
    const [errors, setErrors] = useState<AddCrfErrorState>({ ...ADD_CRF_INITIAL_ERRORS } as any);
    const [page, setPage] = useState({ number: 1, params: '', available: currentCrf ? 2 : 1 });
    const [addCrfState, setAddCrfState] = useState<SVTRule[]>(getInitialRules(currentCrf?.crfRules));

    const isBackBtnDisabled = !!addCrfState.find((item) => !item.isValid);
    const isForcedValidation = !!addCrfState.find((item) => item.forceValidation);

    const [saveCrf] = useMutation(SAVE_STUDY_CRF);

    const disableBtns = useBoolean(!currentCrf);
    const showRulesValidation = useBoolean(false);

    useEffect(() => {
        if (currentCrf) {
            setState({
                id: currentCrf.id,
                configId: currentCrf.configId,
                subjectCode: currentCrf.subjectCode,
                crfName: currentCrf.crfName,
                required: currentCrf.required,
                active: currentCrf.active,
            } as AddCrfFormState);
        }
    }, [currentCrf]);

    const rulesValidation = () => addCrfState.findIndex((item) => !item.isValid) === -1;

    useEffect(() => {
        if (rulesValidation() && disableBtns.value) {
            disableBtns.setFalse();
        }
    }, [addCrfState]);

    const onClose = (action = '') => {
        if (action !== 'error') {
            setState({ ...ADD_CRF_INITIAL_STATE });
            setErrors({ ...ADD_CRF_INITIAL_ERRORS });
        }
        close(action);
    };

    const validate = (value?: string, field?: string, length?: number) => {
        let error = validateFields(value, field, length);

        const configIdError = checkConfigIdErrors(state, crfs, value);
        if (field === 'configId') {
            error = { configId: configIdError };
        }

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (fieldName: string) => (event: any) => {
        const { value } = event.target;
        let length = 50;
        if (fieldName === 'crfName') {
            length = 250;
        }
        if (fieldName === 'configId') {
            length = 24;
        }

        disableBtns.setFalse();
        setState({
            ...state,
            [fieldName]: value,
        });
        validate(value, fieldName, length);
    };

    const onCheckboxChange = (fieldName: 'subjectCode' | 'crfName' | 'required' | 'active') => () => {
        disableBtns.setFalse();
        setState({ ...state, [fieldName]: !state[fieldName] });
    };

    const checkFieldsError = () => {
        const isSubjectCodeExist = checkIfNameExist(
            { ...state, subjectCode: state.subjectCode.trim(), id: null },
            crfs,
            'subjectCode',
        );
        const isCrfNameExist = checkIfNameExist({ ...state, crfName: state.crfName.trim(), id: null }, crfs, 'crfName');
        const checkCurrentSubjectCode =
            currentCrf && currentCrf.subjectCode !== state.subjectCode && isSubjectCodeExist;
        const checkCurrentCrfName = currentCrf && currentCrf.crfName !== state.crfName && isCrfNameExist;

        if (
            ((isSubjectCodeExist || isCrfNameExist) && currentCrf === null) ||
            checkCurrentSubjectCode ||
            checkCurrentCrfName
        ) {
            disableBtns.setTrue();

            if (checkCurrentSubjectCode || isSubjectCodeExist) {
                setErrors({ ...errors, subjectCode: t('admin:CRFSubjectCodeAlreadyExists') });
            }

            if (checkCurrentCrfName || isCrfNameExist) {
                setErrors({ ...errors, crfName: t('admin:CRFNameAlreadyExists') });
            }

            return true;
        }

        return false;
    };

    const submit = () => {
        disableBtns.setTrue();
        if (!rulesValidation()) {
            showRulesValidation.setTrue();
            return;
        }
        if (
            checkRequiredFields({ fields: ['subjectCode', 'crfName'], state, setErrors, errors }) &&
            !checkFieldsError()
        ) {
            saveCrf({
                variables: {
                    studyId,
                    crf: {
                        ...state,
                        crfRules: addCrfState
                            .filter((rule) => rule.field !== ADD_RULES_FIELD_TYPES.default)
                            .map((rule) => ({
                                comparatorField: rule.field,
                                ruleClauseOperator: rule.operator,
                                value: rule.value,
                                ruleClause: rule.andOr,
                                index: rule.index,
                            })),
                    },
                },
                refetchQueries: [
                    {
                        query: GET_STUDY_CRFS,
                        variables: { studyId },
                    },
                ],
            })
                .then(() => {
                    onClose(currentCrf ? 'edit' : 'success');
                })
                .catch(() => {
                    onClose('error');
                });
        }
    };

    const updateStep = (current: number, available: number) => {
        const requiredFields = ['subjectCode', 'crfName'];

        if (currentCrf) {
            requiredFields.push('configId');
            const configIdError = checkConfigIdErrors(state, crfs);

            if (configIdError) {
                setErrors({
                    ...errors,
                    configId: configIdError,
                });
                return;
            }
        }
        if (checkRequiredFields({ fields: requiredFields, state, setErrors, errors }) && !checkFieldsError()) {
            setPage({ number: current, params: '', available });
        }
    };

    const backToCrf = () => {
        showRulesValidation.setTrue();
        if (isBackBtnDisabled) {
            disableBtns.setTrue();
        } else {
            updateStep(1, 2);
        }
    };

    return (
        <CustomDialog
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
            className="custom-width-popup add-crf-modal max-w700"
        >
            <DialogTitle id="add-crf-modal-title">
                {/* We have no recordId in StudyCrf. */}
                {currentCrf ? t('admin:editCrfNumber', { recordId: currentCrf.recordId }) : t('admin:addCrf')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-crf-dialog-content">
                <StepsBox page={page} onChangeStep={() => {}} pageTitles={pageTitles(t)} />
                {page.number === 1 && (
                    <>
                        <div className="checkboxes-wrapper">
                            <div className="left-part">
                                <Checkbox
                                    rounded="none"
                                    id="crf-active-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={state.active}
                                    onChange={onCheckboxChange('active')}
                                    label={t('admin:active')}
                                    disabled={isSysAdmin}
                                    data-testid="crf-active-checkbox"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded="none"
                                    id="crf-required-checkbox"
                                    className="cell-checkbox no-border"
                                    checked={state.required}
                                    onChange={onCheckboxChange('required')}
                                    label={t('ra:required')}
                                    disabled={isSysAdmin}
                                    data-testid="crf-required-checkbox"
                                    size="medium"
                                />
                            </div>
                        </div>
                        <div className="type-edc-wrapper no-mb">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <TextField
                                    label={t('admin:formId')}
                                    id="add-crf-subjectCode"
                                    fullWidth
                                    required
                                    value={state.subjectCode}
                                    onChange={onChange('subjectCode')}
                                    placeholder={t('admin:enterAFormId')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-crf-subjectCode-dialog-help-text"
                                            length={state.subjectCode?.length}
                                            max={50}
                                            error={errors.subjectCode}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.subjectCode}
                                    className="before-select mb"
                                    disabled={isSysAdmin}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type">
                                <TextField
                                    label={t('common:name')}
                                    id="add-crfName"
                                    fullWidth
                                    required
                                    value={state.crfName}
                                    onChange={onChange('crfName')}
                                    placeholder={t('admin:enterName')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-enterCrfName-dialog-help-text"
                                            length={state.crfName?.length}
                                            max={250}
                                            error={errors.crfName}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.crfName}
                                    className="before-select mb"
                                    disabled={isSysAdmin}
                                />
                            </FormControl>
                        </div>
                        {!!currentCrf && (
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type">
                                    <TextField
                                        id="edit-field-config-id"
                                        label={t('admin:configId')}
                                        onChange={onChange('configId')}
                                        fullWidth
                                        value={state.configId}
                                        size="small"
                                        slotProps={{
                                            input: {
                                                readOnly: !isSysAdmin,
                                                endAdornment: <CopyButton value={state.configId ?? ''} />,
                                            },
                                        }}
                                        helperText={
                                            <span style={{ color: '#ae0015' }} className="remaining-text">
                                                {errors.configId}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!errors.configId}
                                    />
                                </FormControl>
                            </div>
                        )}
                    </>
                )}
                <AddRules
                    formState={addCrfState}
                    setFormState={setAddCrfState}
                    studyId={studyId}
                    hidden={page.number !== 2}
                    showValidation={showRulesValidation.value}
                />
                <DialogActions
                    className={cn('add-crf-dialog-content-actions', { 'flex-end': page.number < 2 && !currentCrf })}
                >
                    {page.number === 1 && <ModalUpdatedInfo item={currentCrf} />}
                    {page.number > 1 && (
                        <Button
                            text="backToCrf"
                            id="back-button"
                            disabled={
                                disableBtns.value ||
                                ((showRulesValidation.value || isForcedValidation) && isBackBtnDisabled)
                            }
                            onClick={backToCrf}
                        />
                    )}
                    <div>
                        <Button
                            id="submit-assessment-btn"
                            className="add-crf-btn"
                            disabled={
                                Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                                disableBtns.value ||
                                ((showRulesValidation.value || isForcedValidation) && isBackBtnDisabled)
                            }
                            text={currentCrf ? 'update' : 'save'}
                            onClick={submit}
                        />
                        {page.number < 2 && (
                            <Button
                                id="next-step-crf-btn"
                                className="next-step-crf-btn"
                                text="nextButton"
                                disabled={
                                    Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                                    disableBtns.value ||
                                    isSysAdmin
                                }
                                onClick={() => updateStep(2, 2)}
                            />
                        )}
                    </div>
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default AddCrfDialog;
